<script setup>
import BaseApplicantBio from '@/components/Base/BaseApplicantBio.vue';
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseIcon from '@/components/Base/BaseIcon.vue';
import BaseTooltip from '@/components/Base/BaseTooltip.vue';
import ApplicantActionButtons from '@/components/Elements/Applicant/ApplicantActionButtons.vue';
import { computed } from 'vue';
import { opportunityLabelContext } from '@/composables/useOpportunityLabel';

const props = defineProps({
  applicant: {
    type: Object,
    required: false,
    default: undefined,
  },
  rating: {
    type: Number,
    required: false,
    default: undefined,
  },
  onUnReject: {
    type: Function,
    required: true,
  },
  handleChangeStage: {
    type: Function,
    required: true,
  },
  isHired: {
    type: Boolean,
    required: true,
  },
  isStageChanging: {
    type: Boolean,
    required: true,
  },
  isRejected: {
    type: Boolean,
    required: true,
  },
  isFastTracked: {
    type: Boolean,
    required: false,
  },
  staffingProspect: {
    type: Object,
    required: false,
    default: undefined,
  },
  recruitmentLink: {
    type: String,
    required: false,
    default: undefined,
  },
  onHoverBadge: {
    type: Function,
    required: true,
  },
  adjacentApplications: {
    type: Object,
    required: false,
    default: undefined,
  },
  grade: {
    type: Object,
    required: false,
    default: undefined,
  },
  englishScore: {
    type: String,
    required: false,
    default: undefined,
  },
  codilityScore: {
    type: Number,
    required: false,
    default: undefined,
  },
  isLoading: {
    type: Boolean,
    required: false,
  },
  demandLevel: {
    type: String,
    required: false,
    default: undefined,
  },
  probabilityBasedOn: {
    type: String,
    required: false,
    default: undefined,
  },
  currentSegment: {
    type: Object,
    required: false,
    default: undefined,
  },
});

const emit = defineEmits(['reject']);

const customCompetency = computed(() => props.applicant?.custom_competency);

const opportunityData = computed(
  () => props.currentSegment?.segment?.opportunity
);

const segmentText = computed(() => {
  const segment = props.currentSegment?.segment;
  if (!segment) return null;
  return [
    segment.role,
    segment.primary_skill,
    segment.second_primary_skill,
    segment.region,
  ]
    .filter(Boolean)
    .join(' · ');
});

const segmentSummary = computed(() => props.currentSegment?.segment?.summary);

const segmentAtApplicationDate = computed(
  () => props.currentSegment?.segment_at_application_date
);

const segmentAtApplicationDateText = computed(() => {
  const segment = segmentAtApplicationDate.value;
  if (!segment) return null;
  return [
    segment.role,
    segment.primary_skill,
    segment.second_primary_skill,
    segment.region,
  ]
    .filter(Boolean)
    .join(' · ');
});

const opportunityBadgeColor = {
  very_high: 'Success',
  high: 'Success',
  medium: 'Warning',
  low: 'Error',
  very_low: 'Error',
  unclassified: 'Gray',
};

const supplyDemandContext = {
  'High demand': {
    icon: 'chevron-up-double',
    color: 'Success',
  },
  'High demand role': {
    icon: 'chevron-up-double',
    color: 'Success',
  },
  Oversupply: {
    icon: 'chevron-down-double',
    color: 'Gray',
  },
};

const badges = computed(() =>
  [
    {
      color: 'Error',
      icon: '',
      isActive: props.isRejected,
      label: 'Rejected',
    },
    {
      color: 'Primary',
      icon: '',
      isActive: props.isFastTracked,
      label: 'Fast-track',
    },
    {
      color: supplyDemandContext[props.demandLevel]?.color,
      icon: supplyDemandContext[props.demandLevel]?.icon,
      isActive:
        props.demandLevel && props.probabilityBasedOn !== 'opportunity_score',
      label: props.demandLevel,
    },
    {
      color: 'Warning',
      icon: 'copy-06',
      iconClass: 'text-proxify-warning-500',
      isActive: Boolean(props.applicant?.main_application_id),
      label: 'Duplicate',
    },
  ].filter((item) => item.isActive)
);
</script>
<template>
  <div class="flex justify-between px-8 py-6 bg-proxify-gray-25">
    <div class="flex gap-4">
      <BaseApplicantBio
        :avatar-url="applicant.applicant?.avatar"
        avatar-size="2xl"
        avatar-background-color="white"
        :name="applicant.full_name"
        :supporting-text="applicant.competency"
        name-class="text-display-xs font-semibold font-poppins text-proxify-gray"
        supporting-text-class="!text-body-md !font-normal text-proxify-gray-600"
        :is-loading="isLoading"
      >
        <template
          v-if="customCompetency"
          #supporting-text
        >
          <div
            class="flex items-center gap-1 text-body-md font-normal text-proxify-gray-600"
            data-testid="custom-competency"
          >
            <span>{{ applicant.competency }}</span>
            <span>
              <span class="italic">· Applicant entered:</span>
              {{ customCompetency }}
            </span>
          </div>
        </template>
      </BaseApplicantBio>
      <div class="flex gap-2 mt-1.5 items-center">
        <BaseBadge
          v-for="(item, key) in badges"
          :key="key"
          :color="item.color"
          :icon="item.icon"
          :class="{
            '!pr-2.5': !['Rejected', 'Fast-track'].includes(item.label),
            '!pl-0 !pr-3': item.label === 'Fast-track',
          }"
          class="select-none"
          icon-size="12px"
          :icon-class="item.iconClass"
          size="sm"
          dense
        >
          <template
            v-if="item.label === 'Fast-track'"
            #prepend
          >
            <div
              class="bg-proxify-primary-100 h-6 w-6 rounded-full inline-flex items-center justify-center text-body-xs font-bold"
            >
              F
            </div>
          </template>
          <div class="text-body-sm font-medium">{{ item.label }}</div>
        </BaseBadge>
        <BaseBadge
          v-if="
            probabilityBasedOn === 'opportunity_score' &&
            opportunityData?.value &&
            opportunityLabelContext[opportunityData?.value]
          "
          :color="opportunityBadgeColor[opportunityData?.value]"
          :icon="opportunityLabelContext[opportunityData?.value]?.icon"
          class="!pr-2.5 select-none cursor-pointer"
          icon-size="12px"
          size="sm"
          dense
        >
          <div class="text-body-sm font-medium">
            {{ opportunityData?.label }}
          </div>
          <BaseTooltip
            class="!p-0"
            :offset="[0, 10]"
          >
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-2 p-3">
                <span
                  class="text-[10px] font-semibold uppercase tracking-wide text-proxify-gray-500"
                >
                  {{ segmentAtApplicationDate ? 'Current Segment' : 'Segment' }}
                </span>
                <span class="text-body-xs font-medium text-proxify-gray-700">
                  {{ segmentText }}
                </span>
                <div class="flex items-center gap-1">
                  <div
                    class="w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0"
                  >
                    <BaseIcon
                      :name="
                        opportunityLabelContext[opportunityData?.value]?.icon
                      "
                      :class="
                        opportunityLabelContext[opportunityData?.value]
                          ?.iconClass
                      "
                      size="14px"
                    />
                  </div>
                  <span
                    class="text-body-xs font-semibold"
                    :class="
                      opportunityLabelContext[opportunityData?.value]?.textClass
                    "
                  >
                    {{ opportunityData?.label }}
                  </span>
                </div>
                <p
                  v-if="segmentSummary"
                  class="text-body-xs font-medium text-proxify-gray-600"
                >
                  {{ segmentSummary }}
                </p>
              </div>
              <template v-if="segmentAtApplicationDate">
                <div
                  class="flex flex-col gap-2 rounded-b-lg p-3 bg-proxify-gray-100"
                >
                  <span
                    class="text-[10px] font-semibold uppercase tracking-wide text-proxify-gray-500"
                  >
                    Segment at Application Date
                  </span>
                  <span class="text-body-xs font-medium text-proxify-gray-700">
                    {{ segmentAtApplicationDateText }}
                  </span>
                  <div
                    v-if="
                      segmentAtApplicationDate.opportunity?.value &&
                      opportunityLabelContext[
                        segmentAtApplicationDate.opportunity.value
                      ]
                    "
                    class="flex items-center gap-1"
                  >
                    <div
                      class="w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0"
                    >
                      <BaseIcon
                        :name="
                          opportunityLabelContext[
                            segmentAtApplicationDate.opportunity.value
                          ]?.icon
                        "
                        :class="
                          opportunityLabelContext[
                            segmentAtApplicationDate.opportunity.value
                          ]?.iconClass
                        "
                        size="14px"
                      />
                    </div>
                    <span
                      class="text-body-xs font-semibold"
                      :class="
                        opportunityLabelContext[
                          segmentAtApplicationDate.opportunity.value
                        ]?.textClass
                      "
                    >
                      {{ segmentAtApplicationDate.opportunity.label }}
                    </span>
                  </div>
                  <p
                    v-if="segmentAtApplicationDate.summary"
                    class="text-body-xs font-medium text-proxify-gray-600"
                  >
                    {{ segmentAtApplicationDate.summary }}
                  </p>
                </div>
              </template>
            </div>
          </BaseTooltip>
        </BaseBadge>
        <a
          v-if="staffingProspect"
          v-safe-href="recruitmentLink"
          target="_blank"
          class="self-start"
          data-testid="staffing-prospect-badge"
        >
          <BaseBadge
            icon="link-external-01"
            icon-position="right"
            icon-size="12px"
            icon-class="text-white"
            badge-class="!pl-0 !pr-3 staffing-prospect-badge"
            class="select-none"
            size="sm"
            dense
          >
            <template #prepend>
              <div
                class="bg-proxify-gray-900 h-6 w-6 rounded-full inline-flex items-center justify-center text-body-xs font-bold text-white"
              >
                S
              </div>
            </template>
            <div class="text-body-sm font-medium text-white">
              Staffing prospect
            </div>
            <BaseTooltip max-width="271px">
              <div class="flex flex-col gap-1 text-left">
                <div class="text-body-sm font-bold text-proxify-gray-900">
                  {{ staffingProspect.title }}
                </div>
                <div class="text-body-xs font-normal text-proxify-gray-600">
                  {{ staffingProspect.description }}
                </div>
              </div>
            </BaseTooltip>
          </BaseBadge>
        </a>
      </div>
    </div>
    <ApplicantActionButtons
      :applicant="applicant"
      :on-un-reject="onUnReject"
      :is-hired="isHired"
      :is-stage-changing="isStageChanging"
      :is-rejected="isRejected"
      :handle-change-stage="handleChangeStage"
      @reject="emit('reject', $event)"
    />
  </div>
</template>

<style scoped>
.staffing-prospect-badge {
  background-color: #475467 !important;
  color: #ffffff !important;
  mix-blend-mode: multiply;
}
</style>
